import PropTypes from 'prop-types'
import React, { Component } from 'react'
import cx from 'classnames'
import { withRouter } from 'react-router-dom'
import { injectIntl } from 'react-intl'
import { Layout, Container, Content, PageTitle } from 'src/components/Layout'
import FontAwesomeIconComponent from 'src/components/FontAwesomeIcon/index'
import withToast from 'src/components/Toast'
import { Loader } from 'src/components/Loader'
import { isEmpty, addOrRemoveArray, isSameArray, logError, handleKeyDown } from 'src/utils/helpers'
import intlMessages from '../translations'
import intlMessagesApp from 'src/modules/App/translations'
import styles from './styles.module.less'
import List from 'src/components/List/List'
import { Flex, Text } from '@chakra-ui/react'
import Button from 'src/components/Button/Button'
import ThankYou from 'src/modals/ThankYou/index'

class CreateSlip extends Component {
  constructor(props) {
    super(props)

    this.state = {
      // When "fleet" or "single" are selected (null shows those two options)
      slipType: null,
      isSelectAllChecked: false,
      selectedItems: [],
      successCount: 0,
      successIsFleet: false,
    }
  }

  componentDidMount() {
    const { history, policyDetails } = this.props

    const isSlipCreateAllowed =
      !isEmpty(policyDetails) &&
      policyDetails.hasOwnProperty('isSlipCreateAllowed') &&
      policyDetails.isSlipCreateAllowed === true

    if (!isSlipCreateAllowed) {
      history.push('/documents')
    }
  }

  componentDidUpdate(prevProps) {
    const { intl, slipCreateResponse, clearDocumentFetchTimestamp, toast } = this.props

    // If there is save response and it changed (it's empty object while submitting)
    if (!isEmpty(slipCreateResponse) && slipCreateResponse !== prevProps.slipCreateResponse) {
      const responseData =
        slipCreateResponse.hasOwnProperty('payload') && !isEmpty(slipCreateResponse.payload)
          ? slipCreateResponse.payload
          : {}

      if (slipCreateResponse.success === true) {
        this.setState({
          isSelectAllChecked: false,
          selectedItems: [],
          successCount:
            responseData.hasOwnProperty('countCreated') && responseData.countCreated
              ? responseData.countCreated
              : this.state.successCount,
          successIsFleet: responseData.hasOwnProperty('isFleet') && responseData.isFleet === true,
        })

        // Reset this timer in order to fetch new documents after going to /documents
        clearDocumentFetchTimestamp()
      } else {
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description:
            slipCreateResponse.message || intl.formatMessage(intlMessagesApp.appErrorDescription),
          status: 'error',
        })
      }
    }
  }

  /**
   * Extract policy items (vehicles) from redux state.
   * Which were previously loaded on Policy Summary "full view" page.
   *
   * @return {array}
   */
  getPolicyItems = () => {
    const defaultResponse = []
    try {
      const { policyDetails } = this.props

      // Exit if we don't even know policy details
      if (isEmpty(policyDetails)) {
        return defaultResponse
      }

      // Exit if there are no vehicles (policyItems are vehicles when policy type is "auto")
      if (!policyDetails.hasOwnProperty('policyItems') || isEmpty(policyDetails.policyItems)) {
        return defaultResponse
      }

      // Return policy items (vehicles) eligible to have a slip
      return policyDetails.policyItems.filter(item => {
        return (
          item.hasOwnProperty('isVehicleSlipCreateEligible') &&
          item.isVehicleSlipCreateEligible === true
        )
      })
    } catch (e) {
      logError('Get policy items (vehicles) for creating a slip', e)
      return defaultResponse
    }
  }

  /**
   * Submit selected policy items (vehicles) to create slip/s or fleet slip.
   *
   * @param {Event} event Click event
   */
  createSlip = event => {
    event.preventDefault()

    const { policyDetails } = this.props
    const { slipType, selectedItems } = this.state

    this.props.createSlip({
      slipType: slipType,
      policyId: policyDetails.id,
      policyItemIds: selectedItems,
    })
  }

  /**
   * Get a list of policy items (vehicles) that are allowed to be selected (those which are not disabled).
   *
   * @return {array}
   */
  getAllowedIds = () => {
    try {
      const policyItems = this.getPolicyItems()
      if (isEmpty(policyItems)) {
        return []
      }

      // Get vehicle IDs of allowed/enabled policy items (vehicles)
      return policyItems
        .filter(item => {
          return (
            item.hasOwnProperty('isVehicleSlipCreateEnabled') &&
            item.isVehicleSlipCreateEnabled === true
          )
        })
        .map(item => item.id)
    } catch (e) {
      logError('Get list of allowed items to be selected', e)
      return []
    }
  }

  /**
   * Select/deselect all checkboxes.
   *
   * @param {Event} event Change event
   * @return {boolean}
   */
  onAllCheckboxChange = selectAll => {
    try {
      // If it's deselect all
      if (selectAll) {
        this.setState({
          isSelectAllChecked: false,
          selectedItems: [],
        })
        return true
      }

      // If it's select all
      this.setState({
        isSelectAllChecked: true,
        selectedItems: this.getAllowedIds(),
      })
    } catch (e) {
      logError('Select/deselect all policy items (vehicles) for creating a slip', e)
    }
  }

  /**
   * Select/deselect a vehicle from the list.
   *
   * @param {Event} event Change event
   * @param {mixed} id selected/deselected item ID
   */
  onCheckboxChanged = (checked, id) => {
    try {
      const { isSelectAllChecked } = this.state

      const newlySelectedItems = addOrRemoveArray(this.state.selectedItems, id)

      // Is it select or deselect
      const isListItemChecked = checked
      let newIsSelectAllChecked = isSelectAllChecked
      if (isSelectAllChecked) {
        if (isListItemChecked) {
          // Deselect "select all" checkbox if it was selected and we're deselecting some item from the list
          newIsSelectAllChecked = false
        }
      } else {
        const allowedIds = this.getAllowedIds()

        // If now all items are selected
        if (isSameArray(allowedIds, newlySelectedItems)) {
          newIsSelectAllChecked = true
        }
      }

      this.setState({
        isSelectAllChecked: newIsSelectAllChecked,
        selectedItems: newlySelectedItems,
      })
    } catch (e) {
      logError('Select/deselect a policy item (vehicle) for creating a slip', e)
    }
  }

  /**
   * Go back to documents page in the end.
   *
   * @return {void}
   */
  confirmSuccess = () => {
    this.props.history.push('/documents')
  }

  /**
   * Custom back button since all screens are handled with state instead of custom URL.
   */
  handleBack = () => {
    const { history } = this.props
    const { slipType, successIsFleet, successCount } = this.state

    const isSuccessScreen = successIsFleet || successCount > 0

    if (slipType && !isSuccessScreen) {
      this.setState({
        slipType: null,
      })
      return true
    }

    history.goBack()
  }

  /**
   * Select fleet or single slip type.
   *
   * @param {Event} e
   * @param {string} type
   * @param {boolean} isDisabled Fleet slip action can be disabled
   */
  setSlipType = (e, type, isDisabled) => {
    e.preventDefault()

    if (isDisabled) {
      return true
    }

    this.setState({
      slipType: type,
    })
  }

  getList = policies => {
    const { intl } = this.props
    const { slipType, isSelectAllChecked, selectedItems } = this.state

    const isSingle = slipType === 'single'
    let pols = []

    if (policies && policies.length) {
      console.log(policies, 'test for displaying info')
      pols = policies.map(policy => {
        const isAllowed =
          policy.hasOwnProperty('isVehicleSlipCreateEnabled') &&
          policy.isVehicleSlipCreateEnabled === true
        return {
          ...policy,
          // name: vehicle.year,
          // description: `${vehicle.model} ${vehicle.vin}`,
          name: policy.title || 'Title',
          hideArrow: true,
          disabled: isSingle && !isAllowed,
          checked: isSingle && selectedItems.includes(policy.id),
          leftIcon: isSingle
            ? selectedItems.includes(policy.id)
              ? 'Checked'
              : 'CheckedNo'
            : 'Vehicle',
          onClick: () => {
            if (isSingle && isAllowed) {
              this.onCheckboxChanged(selectedItems.includes(policy.id), policy.id)
            }
            return
          },
        }
      })
    }

    return isSingle
      ? [
          {
            name: intl.formatMessage(intlMessages.policyCreateAllVehicles),
            leftIcon: isSingle && isSelectAllChecked ? 'Checked' : 'CheckedNo',
            checked: isSingle && isSelectAllChecked,
            hideArrow: true,
            onClick: () => {
              this.onAllCheckboxChange(isSelectAllChecked)
              return
            },
          },
          ...pols,
        ]
      : pols
  }

  render() {
    const {
      intl,
      countryIsoCode,
      policyDetails,
      loadingPolicyDetails,
      isSlipCreateInProgress,
    } = this.props

    const { slipType, selectedItems, successCount, successIsFleet } = this.state

    const isSingle = slipType === 'single'
    const isFleet = slipType === 'fleet'
    const isSuccess = successIsFleet || successCount > 0

    // To disable fleet slip button if fleet already exist
    const isFleetSlipCreateEnabled =
      !isEmpty(policyDetails) &&
      policyDetails.hasOwnProperty('isFleetSlipCreateEnabled') &&
      policyDetails.isFleetSlipCreateEnabled === true

    // Get policy items (vehicles)
    const policyItems = this.getPolicyItems()

    const isCanada = countryIsoCode && countryIsoCode.toLowerCase() === 'ca'
    const SlipTypeCard = ({ slipType, title, subtitle, iconType, isDisabled }) => {
      const classNames = cx({
        [styles.slipTypeCard]: true,
        [styles.slipTypeCardDisabled]: isDisabled === true,
      })
      const handleClick = e => this.setSlipType(e, slipType, isDisabled)
      return (
        <Flex
          direction={'column'}
          justify={'center'}
          align={'center'}
          className={classNames}
          tabIndex='0'
          onKeyDown={handleKeyDown(handleClick)}
          onClick={handleClick}
        >
          <div className={styles.slipTypeContent}>
            <FontAwesomeIconComponent icon={iconType} size='sm' type='card-font' />
            <Text fontWeight={500} variant='card' align={'center'} fontSize={'20px'}>
              {title}
            </Text>
            <Text variant='card' align={'center'} mt={'3px'} fontSize={'13px'} opacity={0.6}>
              {subtitle}
            </Text>
          </div>
        </Flex>
      )
    }

    const policySlipTitle = () => {
      if (!slipType) {
        return intl.formatMessage(intlMessages.documentPinkslip)
      }
      if (slipType === 'fleet') {
        return intl.formatMessage(intlMessages.policyCreateCreateFleetPinkSlip)
      }
      if (slipType === 'single') {
        return intl.formatMessage(intlMessages.policyCreateCreateSinglePinkSlip)
      }

      return slipType
    }

    return (
      <Layout>
        <Content>
          <PageTitle
            title={`${intl.formatMessage(intlMessages.policyCreateSlip)} ${policySlipTitle()}`}
            subtitle={intl.formatMessage(intlMessages.documentsPolicySummaryHeadline)}
            goBack={this.handleBack}
          />
          <Container>
            {loadingPolicyDetails || isSlipCreateInProgress ? (
              <Loader styleVariation='staticWithHeight' />
            ) : !slipType ? (
              <div className={styles.slipTypes}>
                <SlipTypeCard
                  slipType='fleet'
                  iconType={['far', 'cars']}
                  iconClassName={styles.fleetIcon}
                  title={
                    isCanada
                      ? intl.formatMessage(intlMessages.policyCreateCreateFleetPinkSlip)
                      : intl.formatMessage(intlMessages.policyCreateCreateFleetEIDCard)
                  }
                  subtitle={intl.formatMessage(
                    intlMessages.policyCreateCreateCreatesOneGenericSlip,
                  )}
                  isDisabled={!isFleetSlipCreateEnabled}
                />
                <SlipTypeCard
                  slipType='single'
                  iconType={['far', 'car']}
                  title={
                    isCanada
                      ? intl.formatMessage(intlMessages.policyCreateCreateSinglePinkSlip)
                      : intl.formatMessage(intlMessages.policyCreateCreateSingleEIDCard)
                  }
                  subtitle={intl.formatMessage(
                    intlMessages.policyCreateCreateCreatesSingleSlipPerVehicle,
                  )}
                />
              </div>
            ) : (
              <>
                {isEmpty(policyItems) ? (
                  <h3 className={styles.noResults}>
                    {intl.formatMessage(intlMessages.documentRemoveVehicleNoResults)}
                  </h3>
                ) : (
                  <div className={styles.checklist}>
                    <List
                      isColumn={true}
                      noMargin={true}
                      isColumnReverse={true}
                      items={this.getList(policyItems)}
                    />
                  </div>
                )}

                <Flex justify={'center'}>
                  <Button
                    size='lg'
                    onClick={this.createSlip}
                    isDisabled={isSingle && selectedItems.length <= 0}
                    type='submit'
                  >
                    {isSingle &&
                      intl.formatMessage(intlMessages.policyCreateCreateSelected, {
                        selectedCount: selectedItems.length,
                      })}
                    {isFleet && intl.formatMessage(intlMessages.policyCreateCreateFleet)}
                  </Button>
                </Flex>

                <ThankYou
                  visible={isSuccess}
                  title={
                    successIsFleet
                      ? intl.formatMessage(intlMessages.policyCreateSuccessMessageFleetSlip)
                      : isCanada
                      ? intl.formatMessage(intlMessages.policyCreateSuccessMessagePinkSlip, {
                          successCount: successCount,
                        })
                      : intl.formatMessage(intlMessages.policyCreateSuccessMessageEIDSlip, {
                          successCount: successCount,
                        })
                  }
                  onClose={this.confirmSuccess}
                  renderContent={<></>}
                />
              </>
            )}
          </Container>
        </Content>
      </Layout>
    )
  }
}

CreateSlip.propTypes = {
  labels: PropTypes.object.isRequired,
  intl: PropTypes.object,
  router: PropTypes.object,
}

export default withRouter(injectIntl(withToast(CreateSlip)))
